/**
 * 3D 构建图 —— 基础零件库
 *
 * 管路产品几乎都是回转体，所以这里以 LatheGeometry（车削面）为主，
 * 通过“轮廓点 → 绕 Y 轴回转”生成管材、管件、密封圈、螺母等零件，
 * 再由 models.js 把它们装配成各系统的爆炸构建图。
 *
 * 约定：所有零件默认沿 Y 轴中心对称，长度居中于原点，单位为“米”量级
 *      （整机模型控制在半径 3 以内，方便统一打光与镜头）。
 */
import * as THREE from 'three'

/* ------------------------------------------------------------------ 材质 */

/**
 * 颜色都按 sRGB 十六进制书写（和设计稿一致），这里统一转成线性空间，
 * 否则在 outputEncoding = sRGB 的渲染管线下深色会被整体提亮、发灰。
 */
function std (hex, opt) {
  const m = new THREE.MeshStandardMaterial(
    Object.assign({ color: hex, side: THREE.DoubleSide }, opt))
  m.color.convertSRGBToLinear()
  return m
}

export const MAT = {
  // 不锈钢：高金属度、低粗糙度
  steel: () => std(0xcdd4d9, { metalness: 0.95, roughness: 0.18 }),
  // 拉丝不锈钢管材（比管件略暗，装配后能分辨管与件）
  steelPipe: () => std(0x8e99a1, { metalness: 0.9, roughness: 0.38 }),
  // 黄铜 / 铜管件
  brass: () => std(0xd7a24c, { metalness: 0.95, roughness: 0.25 }),
  copper: () => std(0xc9793f, { metalness: 0.92, roughness: 0.28 }),
  // 镀锌 / 环氧涂层钢
  galv: () => std(0x8d979e, { metalness: 0.8, roughness: 0.42 }),
  // 密封圈橡胶（EPDM / NBR）
  rubber: (c) => std(c || 0x1e2529, { metalness: 0.0, roughness: 0.85 }),
  // 塑料管材，颜色由各系统指定
  plastic: (c) => std(c, { metalness: 0.05, roughness: 0.45 }),
  // 铝合金中间层
  alu: () => std(0xdadfe3, { metalness: 0.85, roughness: 0.3 }),
  // 保温层 / 发泡
  foam: () => std(0xe8e2d6, { metalness: 0.0, roughness: 0.95 }),
  // 电阻丝
  wire: () => std(0xc0682c, { metalness: 0.9, roughness: 0.35 }),
  // 半透明外壳，用来露出内部结构
  ghost: (c) => {
    const m = new THREE.MeshPhysicalMaterial({
      color: c, metalness: 0.0, roughness: 0.25, transparent: true,
      opacity: 0.34, side: THREE.DoubleSide, depthWrite: false
    })
    m.color.convertSRGBToLinear()
    return m
  }
}

/* -------------------------------------------------------------- 基础形体 */

/** 轮廓点绕 Y 轴回转。pts 为 [[r, y], ...]，首尾相接即得到实心壳体。 */
export function lathe (pts, seg = 96) {
  const v = pts.map(p => new THREE.Vector2(p[0], p[1]))
  const g = new THREE.LatheGeometry(v, seg)
  g.computeVertexNormals()
  return g
}

/** 直管：外径 ro、内径 ri、长度 len，沿 Y 轴居中。 */
export function pipe (ro, ri, len, seg = 96) {
  const h = len / 2
  return lathe([[ri, -h], [ro, -h], [ro, h], [ri, h], [ri, -h]], seg)
}

/** 实心圆柱（沿 Y 轴居中）。 */
export function rod (r, len, seg = 64) {
  const h = len / 2
  return lathe([[0, -h], [r, -h], [r, h], [0, h], [0, -h]], seg)
}

/** O 型密封圈。 */
export function oring (r, t, seg = 96) {
  return new THREE.TorusGeometry(r, t, 20, seg)
}

/** 六角体（螺母 / 卡压六角痕）。 */
export function hex (r, h) {
  return new THREE.CylinderGeometry(r, r, h, 6)
}

/** 带外沿凸台的承口（bell end）：管口扩径段，用于承插 / 卡压结构。 */
export function bell (ri, ro, rBell, len, lip, seg = 96) {
  const h = len / 2
  return lathe([
    [ri, -h], [ro, -h], [ro, h - lip * 2], [rBell, h - lip], [rBell, h],
    [rBell - (ro - ri), h], [rBell - (ro - ri), h - lip],
    [ri, h - lip * 2], [ri, -h]
  ], seg)
}

/** 沟槽管：管壁上滚压出一圈环形沟槽，供卡箍咬合。 */
export function groovedPipe (ro, ri, len, gPos, gDepth, gWidth, seg = 96) {
  const h = len / 2
  const a = gPos - gWidth / 2
  const b = gPos + gWidth / 2
  const rg = ro - gDepth
  return lathe([
    [ri, -h], [ro, -h],
    [ro, a], [rg, a + gDepth * 0.4], [rg, b - gDepth * 0.4], [ro, b],
    [ro, h], [ri, h], [ri, -h]
  ], seg)
}

/* ---------------------------------------------------------------- 组合件 */

/** 扇形壳层：带角度缺口的管壁，用于复合管的“剥层”剖视。 */
export function shellSector (ro, ri, len, phiStart, phiLen, mat) {
  const g = new THREE.Group()
  const h = len / 2
  const v = [[ri, -h], [ro, -h], [ro, h], [ri, h], [ri, -h]]
    .map(p => new THREE.Vector2(p[0], p[1]))
  const wall = new THREE.LatheGeometry(v, 96, phiStart, phiLen)
  wall.computeVertexNormals()
  g.add(new THREE.Mesh(wall, mat))

  // 两端的径向封口，避免剖切处看到空心面片
  ;[phiStart, phiStart + phiLen].forEach(ang => {
    const cap = new THREE.Mesh(new THREE.PlaneGeometry(ro - ri, len), mat)
    cap.position.set(Math.sin(ang) * (ro + ri) / 2, 0, Math.cos(ang) * (ro + ri) / 2)
    cap.rotation.y = ang + Math.PI / 2
    g.add(cap)
  })
  return g
}

/** 半环形卡箍壳体：沿 z=0 平面对开，两侧带螺栓耳，轴向为 Y。 */
export function couplingHalf (rIn, rOut, width, padW, padH) {
  const g = new THREE.Group()
  const shape = new THREE.Shape()
  shape.absarc(0, 0, rOut, 0, Math.PI, false)
  shape.absarc(0, 0, rIn, Math.PI, 0, true)
  const geo = new THREE.ExtrudeGeometry(shape, { depth: width, bevelEnabled: false })
  geo.translate(0, 0, -width / 2)
  geo.rotateX(-Math.PI / 2)
  geo.computeVertexNormals()
  g.add(new THREE.Mesh(geo, MAT.galv()))

  ;[-1, 1].forEach(s => {
    const pad = new THREE.Mesh(new THREE.BoxGeometry(padW, padH, width * 0.26), MAT.galv())
    pad.position.set(s * (rOut * 0.86), 0, -width * 0.13)
    g.add(pad)
  })
  return g
}

/** 螺栓（六角头 + 杆） */
export function bolt (r, len) {
  const g = new THREE.Group()
  const head = new THREE.Mesh(hex(r * 2, r * 1.4), MAT.galv())
  head.position.y = len / 2 + r * 0.7
  const shaft = new THREE.Mesh(rod(r, len), MAT.galv())
  g.add(head, shaft)
  return g
}

/** 阀门手轮 */
export function handwheel (r, t) {
  const g = new THREE.Group()
  const rim = new THREE.Mesh(new THREE.TorusGeometry(r, t, 16, 64), MAT.plastic(0xc0392b))
  rim.rotation.x = Math.PI / 2
  g.add(rim)
  for (let i = 0; i < 2; i++) {
    const spoke = new THREE.Mesh(new THREE.BoxGeometry(r * 2, t * 0.8, t * 1.2), MAT.plastic(0xc0392b))
    spoke.rotation.y = (i * Math.PI) / 2
    g.add(spoke)
  }
  const hub = new THREE.Mesh(rod(t * 1.6, t * 3), MAT.plastic(0xc0392b))
  g.add(hub)
  return g
}

/** 螺旋电阻丝（电熔套筒内嵌） */
export function heliCoil (r, len, turns, wireR = 0.02) {
  const pts = []
  const n = turns * 40
  for (let i = 0; i <= n; i++) {
    const t = i / n
    const a = t * turns * Math.PI * 2
    pts.push(new THREE.Vector3(Math.cos(a) * r, (t - 0.5) * len, Math.sin(a) * r))
  }
  return new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), n, wireR, 8, false)
}

/** 沿路径生成的弯管（用于弯头、三通支管） */
export function bentTube (points, ro, seg = 120) {
  const curve = new THREE.CatmullRomCurve3(points.map(p => new THREE.Vector3(...p)))
  return new THREE.TubeGeometry(curve, seg, ro, 48, false)
}

/** 把若干 mesh 合成一个带元数据的零件组 */
export function part (name, note, objects, explode) {
  const g = new THREE.Group()
  ;[].concat(objects).forEach(o => g.add(o))
  g.userData = {
    name,
    note,
    explode: new THREE.Vector3(...(explode || [0, 0, 0])),
    home: new THREE.Vector3()
  }
  return g
}
